import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  ImageOverlay,
  Rectangle,
  Circle,
  Polygon,
  Polyline,
  Marker,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import finishIconUrl from "../assets/icons/navigation/finish.svg";

import stairsIcon from "../assets/icons/navigation/stairs.svg";
import elevatorIcon from "../assets/icons/navigation/elevator.svg";
import bathroomIcon from "../assets/icons/navigation/bathroom.svg";
import roomIcon from "../assets/icons/addlocation/room.svg";
import qrIcon from "../assets/icons/layout1/qrcode.svg";
import { applyMarkerOffsets } from "../utils/markerClusterUtil";

const px = (x, y, imgH) => [imgH - y, x];

function ClickHandler({ imgH, onMapClick }) {
  useMapEvents({
    click: (e) => {
      if (!onMapClick) return;
      const target = e.originalEvent?.target;
      if (target && target.closest(".leaflet-marker-icon")) return;
      const pxCoord = { x: e.latlng.lng, y: imgH - e.latlng.lat };
      onMapClick(pxCoord, e);
    },
  });
  return null;
}

function FreehandHandler({ imgH, enabled, onStart, onMove, onEnd }) {
  const map = useMap();

  useEffect(() => {
    if (!enabled) {
      map.dragging.enable();
      map.getContainer().style.cursor = "";
      return undefined;
    }
    map.dragging.disable();
    map.getContainer().style.cursor = "crosshair";
    return () => {
      map.dragging.enable();
      if (map.getContainer()) map.getContainer().style.cursor = "";
    };
  }, [enabled, map]);

  const toPoint = (latlng) => ({ x: latlng.lng, y: imgH - latlng.lat });

  useMapEvents({
    mousedown: (e) => { if (!enabled) return; L.DomEvent.preventDefault(e); onStart?.(toPoint(e.latlng), e); },
    mousemove: (e) => { if (!enabled) return; onMove?.(toPoint(e.latlng), e); },
    mouseup:   (e) => { if (!enabled) return; onEnd?.(toPoint(e.latlng), e); },
    mouseout:  (e) => { if (!enabled) return; onEnd?.(toPoint(e.latlng), e); },
  });

  return null;
}

const denorm = (v, scale) => (v <= 1 ? v * scale : v);

function FitBoundsOnChange({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (!bounds) return;
    map.invalidateSize();
    map.fitBounds(bounds, { animate: false, padding: [10, 10] });
    const t = setTimeout(() => {
      map.invalidateSize();
      map.fitBounds(bounds, { animate: false, padding: [10, 10] });
    }, 120);
    return () => clearTimeout(t);
  }, [bounds, map]);
  return null;
}

function FocusPointOnChange({ point, imgH, focusKey, zoomOffset = 2 }) {
  const map = useMap();
  const focusZoomRef = useRef(null);

  useEffect(() => {
    if (!point || !isFinite(point.x) || !isFinite(point.y)) return undefined;
    const target = [imgH - point.y, point.x];
    if (focusZoomRef.current === null) {
      focusZoomRef.current = Math.min(
        map.getMaxZoom(),
        Math.max(map.getZoom() + zoomOffset, 0.3)
      );
    }
    const zoom = focusZoomRef.current;
    const t = setTimeout(() => {
      map.invalidateSize();
      // Apply focus without a transition. A transition may finish after React
      // has replaced the map and try to read an already removed map pane.
      map.setView(target, zoom, { animate: false });
    }, 180);
    return () => clearTimeout(t);
  }, [point, imgH, focusKey, zoomOffset, map]);

  return null;
}

function shapeToPixel(shape, imgW, imgH) {
  const out = { ...shape };
  if (typeof shape.x === "number") out.x = denorm(shape.x, imgW);
  if (typeof shape.y === "number") out.y = denorm(shape.y, imgH);
  if (typeof shape.width === "number") out.width = denorm(shape.width, imgW);
  if (typeof shape.height === "number") out.height = denorm(shape.height, imgH);
  if (Array.isArray(shape.points)) {
    out.points = shape.points.map((p) => ({
      x: denorm(p.x, imgW),
      y: denorm(p.y, imgH),
    }));
  }
  return out;
}

function makeIcon({ emoji, iconUrl, color = "#2563eb", size = 36 }) {
  const content = iconUrl
    ? `<img src="${iconUrl}" style="width:60%;height:60%;filter:brightness(0) invert(1);" />`
    : emoji;
  return L.divIcon({
    className: "leaflet-floor-marker",
    html: `<div style="
      width:${size}px;height:${size}px;
      display:flex;align-items:center;justify-content:center;
      background:${color};color:white;
      border:2px solid white;border-radius:10px;
      box-shadow:0 2px 6px rgba(0,0,0,.3);
      font-size:${Math.floor(size * 0.55)}px;
      font-weight:bold;
    ">${content}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function makeDynamicIcon(type, label, imageUrl, isOffsetted = false, showLabel = false) {
  let content = "";
  let bgColor = "#7c3aed";
  let size = isOffsetted ? 28 : 36; // ขนาดเล็กลงเมื่อมี offsetting (overlap)
  let isCircle = true;
  let opacity = 1;

  if (type === "qr") {
    bgColor = "white"; isCircle = false;
    content = `<img src="${qrIcon}" style="width:60%;height:60%;" />`;
  } else if (type === "stair" || type === "stairs") {
    bgColor = "#ea580c";
    content = `<img src="${stairsIcon}" style="width:60%;height:60%;filter:brightness(0) invert(1);" />`;
  } else if (type === "elevator") {
    bgColor = "#16a34a";
    content = `<img src="${elevatorIcon}" style="width:60%;height:60%;filter:brightness(0) invert(1);" />`;
  } else if (type === "bathroom" || type === "toilet") {
    bgColor = "#2563eb";
    content = `<img src="${bathroomIcon}" style="width:60%;height:60%;filter:brightness(0) invert(1);" />`;
  } else if (type === "counterservice" || type === "room") {
    bgColor = "#7c3aed";
    content = `<img src="${roomIcon}" style="width:60%;height:60%;filter:brightness(0) invert(1);" />`;
  } else {
    bgColor = "#7c3aed"; content = "📌";
  }

  if (imageUrl) content = `<img src="${imageUrl}" style="width:100%;height:100%;object-fit:cover;" />`;

  const borderStyle = isCircle ? "border-radius: 50%;" : "border-radius: 8px;";
  const whiteBorder = isCircle ? "border: 2.5px solid white;" : "";
  const shadowStyle = isCircle
    ? "box-shadow: 0 0 0 1px rgba(0,0,0,0.1), 0 4px 10px rgba(0,0,0,0.15);"
    : "box-shadow: 0 4px 10px rgba(0,0,0,0.15);";

  // แสดง label ถ้า: ไม่ถูก offset หรือ showLabel=true (ปลายทางสำคัญ)
  const shouldShowLabel = label && (!isOffsetted || showLabel);

  return L.divIcon({
    className: `leaflet-floor-custom-marker-${type}${isOffsetted ? "-offsetted" : ""}`,
    html: `<div style="position:absolute;left:0;top:0;display:flex;align-items:center;gap:6px;opacity:${opacity};">
      <div style="width:${size}px;height:${size}px;background:${bgColor};${borderStyle}${whiteBorder}${shadowStyle}display:flex;align-items:center;justify-content:center;overflow:hidden;">
        ${content}
      </div>
      ${shouldShowLabel ? `<div style="background:rgba(80,80,80,0.85);color:white;font-family:sans-serif;font-size:14px;font-weight:bold;padding:6px 14px;border-radius:9999px;white-space:nowrap;box-shadow:0 4px 10px rgba(0,0,0,0.15);">${label}</div>` : ""}
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function makeRouteStepIcon({ rotation = 0, size = 36, type = "step" } = {}) {
  const isFinish = type === "arrive";
  const bgColor = isFinish ? "#22c55e" : "#2563eb";
  const shadowColor = isFinish ? "rgba(34,197,94,.4)" : "rgba(37,99,235,.4)";
  const rippleColor = isFinish ? "rgba(34,197,94,.35)" : "rgba(37,99,235,.35)";

  const contentHtml = isFinish
    ? `<span style="
        width:${Math.round(size * 0.55)}px;height:${Math.round(size * 0.55)}px;
        display:block;background:white;
        -webkit-mask:url('${finishIconUrl}') center / contain no-repeat;
        mask:url('${finishIconUrl}') center / contain no-repeat;
      "></span>`
    : `<svg class="route-step-arrow" viewBox="0 0 24 24" width="${Math.round(size * 0.62)}" height="${Math.round(size * 0.62)}" fill="none" xmlns="http://www.w3.org/2000/svg" style="
        transform: rotate(${rotation}deg);
        transform-origin: center;
        transition: transform 0.1s ease-out;
        filter: drop-shadow(0px 1px 2px rgba(0,0,0,0.3));
        will-change: transform;
      ">
        <path d="M12 2L19 21L12 17.2L5 21L12 2Z" fill="white" stroke="#1d4ed8" stroke-width="1.2" stroke-linejoin="round"/>
      </svg>`;

  return L.divIcon({
    className: "leaflet-route-step-marker",
    html: `<div style="
      width:${size}px;height:${size}px;
      position:relative;
      display:flex;align-items:center;justify-content:center;
      background:${bgColor};
      border:2.5px solid #ffffff;
      border-radius:9999px;
      box-shadow:0 3px 10px ${shadowColor};
    ">
      <span class="route-step-ripple" style="border-color:${rippleColor};"></span>
      <span class="route-step-ripple route-step-ripple-delay" style="border-color:${rippleColor};"></span>
      ${contentHtml}
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function makeUserHeadingIcon() {
  return L.divIcon({
    className: "leaflet-user-heading-marker",
    html: `<div style="
      width:44px;height:44px;
      position:relative;
      display:flex;align-items:center;justify-content:center;
    ">
      <span style="
        position:absolute;
        inset:0;
        border-radius:9999px;
        background:rgba(37,99,235,.2);
        border:2px solid rgba(255,255,255,.9);
        box-shadow:0 2px 10px rgba(15,23,42,.25);
      "></span>
      <svg class="user-heading-arrow" viewBox="0 0 24 24" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" style="
        position:relative;
        z-index:2;
        transform:rotate(0deg);
        transform-origin:center;
        transition:transform 0.15s ease-out;
        filter: drop-shadow(0px 1px 2px rgba(0,0,0,0.4));
      ">
        <path d="M12 2L19 21L12 17.2L5 21L12 2Z" fill="#2563eb" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/>
      </svg>
      <span style="
        position:absolute;
        z-index:3;
        width:6px;height:6px;
        border-radius:9999px;
        background:#ffffff;
      "></span>
    </div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
}

function UserHeadingMarker({ point, heading, imgH }) {
  const markerRef = useRef(null);
  const icon = useMemo(() => makeUserHeadingIcon(), []);

  useEffect(() => {
    const markerElement = markerRef.current?.getElement();
    const arrow = markerElement?.querySelector(".user-heading-arrow");
    if (!arrow) return;
    const rotation = Number.isFinite(heading) ? heading : 0;
    arrow.style.transform = `rotate(${rotation}deg)`;
  }, [heading]);

  return (
    <Marker
      ref={markerRef}
      position={px(point.x, point.y, imgH)}
      icon={icon}
      zIndexOffset={1000}
    >
      <Tooltip direction="top" offset={[0, -20]} permanent>
        คุณอยู่ที่นี่
      </Tooltip>
    </Marker>
  );
}

function AnimatedRouteStepMarker({ marker, imgH }) {
  const map = useMap();
  const leafletMarkerRef = useRef(null);
  const previousRef = useRef(null);
  const frameRef = useRef(null);

  // Directly update rotation transform for 60fps instant response when rotating phone
  useEffect(() => {
    if (!leafletMarkerRef.current || !marker) return;
    const el = leafletMarkerRef.current.getElement();
    const arrow = el?.querySelector(".route-step-arrow");
    if (arrow) {
      arrow.style.transform = `rotate(${marker.rotation || 0}deg)`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker?.rotation]);

  // Position & type transition animation (only triggered when step position or type changes)
  useEffect(() => {
    if (!marker) return undefined;

    const toLatLng = (point) => L.latLng(imgH - point.y, point.x);
    const duration = marker.type === "arrive" ? 520 : 420;
    const nextLatLng = toLatLng(marker);

    if (!leafletMarkerRef.current) {
      leafletMarkerRef.current = L.marker(nextLatLng, {
        icon: makeRouteStepIcon({
          rotation: marker.rotation || 0,
          type: marker.type,
        }),
        interactive: false,
      }).addTo(map);
      previousRef.current = marker;
      return () => {};
    }

    const previous = previousRef.current;
    if (previous && previous.x === marker.x && previous.y === marker.y && previous.type === marker.type) {
      return () => {};
    }

    const start = previous || marker;
    const startLatLng = toLatLng(start);
    const startedAt = performance.now();

    leafletMarkerRef.current.setIcon(
      makeRouteStepIcon({
        rotation: marker.rotation || 0,
        type: marker.type,
      })
    );

    if (frameRef.current) cancelAnimationFrame(frameRef.current);

    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      const lat = startLatLng.lat + (nextLatLng.lat - startLatLng.lat) * eased;
      const lng = startLatLng.lng + (nextLatLng.lng - startLatLng.lng) * eased;

      leafletMarkerRef.current?.setLatLng([lat, lng]);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        previousRef.current = marker;
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker?.x, marker?.y, marker?.type, imgH, map]);

  useEffect(() => {
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (leafletMarkerRef.current) {
        leafletMarkerRef.current.remove();
        leafletMarkerRef.current = null;
      }
    };
  }, []);

  return null;
}

const ICONS = {
  restroom: makeIcon({ emoji: "WC", color: "#0ea5e9" }),
  qr:       makeIcon({ emoji: "📍", color: "#2563eb" }),
  stair:    makeIcon({ iconUrl: stairsIcon, color: "#ea580c" }),
  elevator: makeIcon({ iconUrl: elevatorIcon, color: "#16a34a" }),
  bathroom: makeIcon({ iconUrl: bathroomIcon, color: "#2563eb" }),
  poi:      makeIcon({ emoji: "📌", color: "#7c3aed" }),
  user:     makeIcon({ emoji: "🧍", color: "#dc2626", size: 44 }),
  closure:  makeIcon({ emoji: "🚧", color: "#dc2626", size: 38 }),
};

function PanToActiveStep({ activePoint, imgH }) {
  const map = useMap();
  useEffect(() => {
    if (!activePoint) return;
    map.panTo([imgH - activePoint.y, activePoint.x], { animate: true, duration: 0.5 });
  }, [activePoint, imgH, map]);
  return null;
}

export default function LeafletFloorMap({
  imageUrl,
  imageWidth,
  imageHeight,
  shapes = [],
  markers = [],
  corridorPaths = [],
  onCorridorClick = null,
  draftPath = [],
  routePath = null,
  userPosition = null,
  userHeading = null,
  onMapClick = null,
  routeStepMarker = null,
  freehandEnabled = false,
  onFreehandStart = null,
  onFreehandMove = null,
  onFreehandEnd = null,
  className = "",
  minZoom = -3,
  maxZoom = 4,
  currentStepIndex = null,
  showShapes = true,
  focusPoint = null,
  focusKey = null,
  focusZoomOffset = 2,
  children,
}) {
  const [detectedSize, setDetectedSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!imageUrl) return;
    setDetectedSize({ w: 0, h: 0 });
    const im = new window.Image();
    im.onload = () => setDetectedSize({ w: im.naturalWidth, h: im.naturalHeight });
    im.src = imageUrl;
  }, [imageUrl]);

  const realW = detectedSize.w || imageWidth || 0;
  const realH = detectedSize.h || imageHeight || 0;
  const dimsReady = realW > 0 && realH > 0;
  const imgW = realW || 1000;
  const imgH = realH || 700;

  const distanceBetween = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  // safe routePath — กัน undefined point
  const safeRoutePath = useMemo(() => {
    if (!routePath) return [];
    return routePath.filter(p => p && isFinite(p.x) && isFinite(p.y));
  }, [routePath]);

  const stepPoints = useMemo(() => {
    if (safeRoutePath.length < 2) return [];
    return safeRoutePath.reduce((points, point) => {
      const previous = points[points.length - 1];
      if (!previous || distanceBetween(previous, point) >= 12) points.push(point);
      return points;
    }, []);
  }, [safeRoutePath]);

  const activeStepPoint = useMemo(() => {
    if (currentStepIndex === null || currentStepIndex === undefined) return null;
    return stepPoints[currentStepIndex] || null;
  }, [stepPoints, currentStepIndex]);


  const [animatedPoint, setAnimatedPoint] = useState(null);

  useEffect(() => {
    if (!activeStepPoint) { setAnimatedPoint(null); return; }
    if (!animatedPoint) { setAnimatedPoint(activeStepPoint); return; }

    const startX = animatedPoint.x, startY = animatedPoint.y;
    const endX = activeStepPoint.x, endY = activeStepPoint.y;
    const duration = 400;
    const startTime = performance.now();
    let animId;

    const frame = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setAnimatedPoint({ x: startX + (endX - startX) * ease, y: startY + (endY - startY) * ease });
      if (progress < 1) animId = requestAnimationFrame(frame);
    };

    animId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(animId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepPoint]);

  const isArrived = useMemo(() => (
    currentStepIndex !== null &&
    currentStepIndex !== undefined &&
    stepPoints.length > 0 &&
    currentStepIndex === stepPoints.length - 1
  ), [stepPoints, currentStepIndex]);

  const pulseStyle = useMemo(() => {
    const c  = isArrived ? "rgba(16,185,129,0.7)" : "rgba(56,189,248,0.7)";
    const c0 = isArrived ? "rgba(16,185,129,0)"   : "rgba(56,189,248,0)";
    return (
      <style>{`
        @keyframes leaflet-active-step-pulse {
          0%   { transform:scale(0.95); box-shadow:0 0 0 0 ${c}; }
          70%  { transform:scale(1.1);  box-shadow:0 0 0 10px ${c0}; }
          100% { transform:scale(0.95); box-shadow:0 0 0 0 ${c0}; }
        }
        .leaflet-active-step-marker .leaflet-step-pulse-container {
          animation: leaflet-active-step-pulse 1.8s infinite;
        }
      `}</style>
    );
  }, [isArrived]);

  const bounds = useMemo(() => [[0, 0], [imgH, imgW]], [imgW, imgH]);

  const pixelShapes = useMemo(() =>
    shapes.filter(s => !s.hidden).map(s => shapeToPixel(s, imgW, imgH)),
    [shapes, imgW, imgH]
  );

  // Memoize markers rendering with overlap detection
  const renderedMarkers = useMemo(() => {
    const validMarkers = markers.filter(m => m && isFinite(m.x) && isFinite(m.y));
    const offsetMarkers = applyMarkerOffsets(validMarkers);

    return offsetMarkers.map((m) => {
      const isDynamic = ["qr","stair","stairs","elevator","bathroom","toilet","counterservice","poi","room"].includes(m.type);
      const icon = isDynamic ? makeDynamicIcon(m.type, m.label || m.name, m.imageUrl, m.isOffsetted, m.showLabel) : (ICONS[m.type] || ICONS.poi);
      const displayX = m.displayX ?? m.x;
      const displayY = m.displayY ?? m.y;

      return (
        <Marker
          key={`${m.id}-${m.type}-${m.label || m.name}-${m.imageUrl || ""}`}
          position={px(displayX, displayY, imgH)}
          icon={icon}
          eventHandlers={m.onClick ? { click: (e) => { L.DomEvent.stopPropagation(e); m.onClick(m); } } : {}}
          zIndexOffset={m.isOffsetted ? 500 : 0}
        >
          {!isDynamic && (m.label || m.name) && (
            <Tooltip direction="right" offset={[12, 0]} permanent={!!m.permanent}>
              {m.label || m.name}
            </Tooltip>
          )}
        </Marker>
      );
    });
  }, [markers, imgH]);

  if (!imageUrl) return (
    <div className={`flex items-center justify-center bg-slate-100 ${className}`}>
      <div className="text-slate-400 text-sm">ไม่มีรูปแผนผัง</div>
    </div>
  );

  if (!dimsReady) return (
    <div className={`flex items-center justify-center bg-slate-100 ${className}`}>
      <div className="text-slate-400 text-sm">กำลังโหลดแผนที่...</div>
    </div>
  );

  return (
    <MapContainer
      key={`${imgW}-${imgH}`}
      crs={L.CRS.Simple}
      bounds={bounds}
      minZoom={minZoom}
      maxZoom={maxZoom}
      zoomSnap={0.25}
      zoomDelta={0.5}
      attributionControl={false}
      zoomControl={false}
      className={className}
      style={{ background: "#f1f5f9" }}
    >
      <FitBoundsOnChange bounds={bounds} />
      <FocusPointOnChange
        point={focusPoint}
        imgH={imgH}
        focusKey={focusKey}
        zoomOffset={focusZoomOffset}
      />
      {onMapClick && <ClickHandler imgH={imgH} onMapClick={onMapClick} />}
      {freehandEnabled && (
        <FreehandHandler
          imgH={imgH} enabled={freehandEnabled}
          onStart={onFreehandStart} onMove={onFreehandMove} onEnd={onFreehandEnd}
        />
      )}

      <ImageOverlay url={imageUrl} bounds={bounds} opacity={1} />
      <style>{`
        .leaflet-route-step-marker {
          overflow: visible;
        }
        .leaflet-route-step-marker .route-step-ripple {
          position: absolute;
          inset: -2px;
          z-index: -1;
          border: 2px solid;
          border-radius: 9999px;
          opacity: 0;
          animation: route-step-ripple 1.55s ease-out infinite;
          pointer-events: none;
        }
        .leaflet-route-step-marker .route-step-ripple-delay {
          animation-delay: 0.78s;
        }
        @keyframes route-step-ripple {
          0% {
            opacity: 0.65;
            transform: scale(1);
          }
          70% {
            opacity: 0.12;
          }
          100% {
            opacity: 0;
            transform: scale(2.25);
          }
        }
      `}</style>

      {/* shapes */}
      {showShapes && pixelShapes.map((s) => {
        const color = s.color || "#9ca3af";
        const common = {
          interactive: !freehandEnabled,
          pathOptions: { color, fillColor: color, fillOpacity: 0.45, weight: 1.5 },
        };
        if (s.type === "rectangle" && Array.isArray(s.points)) {
          return (
            <Polygon key={s.id} positions={s.points.map(p => px(p.x, p.y, imgH))} {...common}>
              {s.name && <Tooltip>{s.name}</Tooltip>}
            </Polygon>
          );
        }
        if (s.type === "rectangle") {
          const { x=0, y=0, width:w=0, height:h=0 } = s;
          return (
            <Rectangle key={s.id} bounds={[px(x, y+h, imgH), px(x+w, y, imgH)]} {...common}>
              {s.name && <Tooltip>{s.name}</Tooltip>}
            </Rectangle>
          );
        }
        if (s.type === "circle") {
          const { x=0, y=0, width:w=0, height:h=0 } = s;
          return (
            <Circle key={s.id} center={px(x+w/2, y+h/2, imgH)} radius={Math.max(w,h)/2} {...common}>
              {s.name && <Tooltip>{s.name}</Tooltip>}
            </Circle>
          );
        }
        if (s.type === "triangle") {
          const { x=0, y=0, width:w=0, height:h=0 } = s;
          return (
            <Polygon key={s.id} positions={[px(x+w/2,y,imgH), px(x,y+h,imgH), px(x+w,y+h,imgH)]} {...common}>
              {s.name && <Tooltip>{s.name}</Tooltip>}
            </Polygon>
          );
        }
        return null;
      })}

      {/* corridor paths */}
      {corridorPaths.map((path, index) => {
        const points = Array.isArray(path.points) ? path.points : [];
        if (points.length < 2) return null;
        return (
          <Polyline
            key={path.id || index}
            interactive={!freehandEnabled}
            positions={points.map(p => px(p.x, p.y, imgH))}
            pathOptions={{ color: path.color || "#f97316", weight: 5, opacity: 0.9 }}
            eventHandlers={{ click: (e) => { if (onCorridorClick) { L.DomEvent.stopPropagation(e); onCorridorClick(path); } } }}
          >
            {(path.name || path.label) && <Tooltip>{path.name || path.label}</Tooltip>}
          </Polyline>
        );
      })}

      {draftPath.length > 1 && (
        <Polyline
          interactive={false}
          positions={draftPath.map(p => px(p.x, p.y, imgH))}
          pathOptions={{ color: "#fb923c", weight: 5, opacity: 0.9 }}
        />
      )}

      {/* route — background */}
      {safeRoutePath.length > 1 && (
        <Polyline
          interactive={false}
          positions={safeRoutePath.map(p => px(p.x, p.y, imgH))}
          pathOptions={{ color: "#1e3a8a", weight: 6, opacity: 0.95, lineCap: "round", lineJoin: "round" }}
        />
      )}

      {/* route — animated dashes */}
      {safeRoutePath.length > 1 && (
        <Polyline
          interactive={false}
          positions={safeRoutePath.map(p => px(p.x, p.y, imgH))}
          pathOptions={{ className: "leaflet-animated-route-flow", color: "#60a5fa", weight: 3.5, opacity: 0.95, lineCap: "round", lineJoin: "round" }}
        />
      )}
      {pulseStyle}
      {activeStepPoint && <PanToActiveStep activePoint={activeStepPoint} imgH={imgH} />}

      {routeStepMarker && (
        <AnimatedRouteStepMarker marker={routeStepMarker} imgH={imgH} />
      )}

      {/* markers with overlap detection */}
      {renderedMarkers}

      {/* user position */}
      {userPosition && isFinite(userPosition.x) && isFinite(userPosition.y) && !routeStepMarker && (
        <UserHeadingMarker point={userPosition} heading={userHeading} imgH={imgH} />
      )}

      {children}
    </MapContainer>
  );
}
